import { ChangeDetectorRef, Component, effect, ElementRef, Input, QueryList, ViewChild, ViewChildren, viewChild, WritableSignal, signal } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { ActivatedRoute, Router } from '@angular/router';
import { ReadMoreComponent } from '@components/read-more/read-more.component';
import { ApiClientConstant } from '@cureskin/api-client';
import { InstantCheckup } from '@cureskin/api-client/src/server-api';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { CurrentComponentService } from '@services/current-component';
import { DataStoreService } from '@services/data-store-service';
import { EventLoggerService } from '@services/event-logger-service';
import { PRIMARY_CHANNELS } from '@services/event-logger-service/event-logger.constant';
import { ExperimentService } from '@services/experiment/experiment.service';
import { FollowUpService } from '@services/followup-service/followup.service';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { LocalStorageService } from '@services/local-storage-service';
import { ReAcquisitionService } from '@services/re-acquisition-service/re-acquisition.service';
import { SelfCheckupService } from '@services/self-checkup-service';
import { WindowRefService } from '@services/window-ref-service';
import { BothSideFaceBottomSheetComponent } from '@shared/bottom-sheet-layouts/both-side-face-bottom-sheet/both-side-face-bottom-sheet.component';
import { DeletePhotoSheetComponent } from '@shared/bottom-sheet-layouts/delete-photo-sheet/delete-photo-sheet.component';
import { MoreInsightsBottomsheetComponent} from '@shared/bottom-sheet-layouts/more-insights-bottomsheet/more-insights-bottomsheet.component';
import { SideFacePhotoBottomSheetComponent } from '@shared/bottom-sheet-layouts/side-face-photo-bottom-sheet/side-face-photo-bottom-sheet.component';
import { WrongPhotoDetectedComponent } from '@shared/bottom-sheet-layouts/wrong-photo-detected/wrong-photo-detected.component';
import {
  FollowUpQuestionsSheetComponent,
  FollowUpQuestionsSheetData,
} from '@shared/follow-up-questions-sheet/follow-up-questions-sheet.component';
import { InstantCheckupStatusBannerStateEnum, HeaderTypeEnum, ButtonVariant, FooterLayout, FooterContainerVariant, I18nKey, InvalidPhotoUiCode, InvalidPhotoErrorMapping, InvalidPhotoErrorMappingVariant, UI_CODE_CATEGORY_MAP } from '@shared/types/instant-checkup.types';
import { AppConfig } from 'client/app/app.config';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { SwiperOptions } from 'swiper';
import { SwiperComponent } from 'swiper/angular';

import { AllocatedRegimen } from '../../../app-constant-types';
import { problemsCopy, concernSeverityTranslations } from '../../problem-details';
import { AiDetectionResult, DisplayDetectionResult, PolygonEntry, ScalpViewMode, TEAL_DETECTION_COLOR } from './checkup-view-constant';
import { FACE_PROBLEM_CLASS_MAPPING } from './face-problem-class-mappings';


interface ConcernSeverity {
  'Mild': string;
  'Moderate': string;
  'Severe': string;
}

interface ConcernSeverityThreshold {
  [key: string]: {
    mild: number;
    moderate: number;
  };
}

interface Point {
  x: number;
  y: number;
}

@Component({
  selector: 'app-checkup-view-experiment',
  templateUrl: './checkup-view-experiment.component.html',
  styles: [':host {@apply tw-flex tw-flex-col tw-h-full tw-bg-gray-100;}'],
  standalone: false,
})
export class CheckupViewExperimentComponent {
  @Input() isFromListView: boolean = false;
  @Input('experiments') experiments: any[];
  @Input() instantCheckup: any;
  @ViewChild('cameraInput', { static: true }) cameraInput: ElementRef;
  @ViewChild('readMoreComponent', { static: true }) readMoreComponent: ReadMoreComponent;
   
  @ViewChildren(ReadMoreComponent) readMoreComponents!: QueryList<ReadMoreComponent>;
  @ViewChild('swiperRef', { static: false }) swiper: SwiperComponent;
  image: HTMLImageElement;
  faceLandmarks: any;
  ui: any = {};
  user: any;
  results: DisplayDetectionResult[] = [];
  croppingConfig: { top: number, left: number, ratio: number, offsetX?: number, offsetY?: number };
  selectedDetection: DisplayDetectionResult | null;
  problemsCopy: { [key: string]: { [key: string]: { name: string, info: string } } } = problemsCopy;
  concernSeverityTranslations: { [key: string]: { [key: string]: string } } = concernSeverityTranslations;
  loading: boolean = true;
  tag: any;
  consultationSession: any;

  carouselConfig: SwiperOptions = {
    slidesPerView: 1.1,
    navigation: { prevEl: 'nonext', nextEl: 'nonext' },
    autoplay: false,
    height: 120,
  };

  concernSeverity: ConcernSeverity = {
    Mild: 'Mild',
    Moderate: 'Moderate',
    Severe: 'Severe',
  };
  concernSeverityThreshold: ConcernSeverityThreshold = {
    'pimple-region': {
      mild: 2,
      moderate: 5,
    },
    'darkspot-region': {
      mild: 4,
      moderate: 8,
    },
    'come-region': {
      mild: 4,
      moderate: 8,
    },
    'ascar-region': {
      mild: 2,
      moderate: 5,
    },
    PerioralPigmentation: { // Always severe for now
      mild: 0,
      moderate: 0,
    },
    OpenPores: { // Always Moderate for now
      mild: 0,
      moderate: 100,
    },
  };
  faceType: string;
  shouldShowIncorrectPhotoDetectedSheet: boolean;
  doesPhotoTypeMatches: boolean;
  instantCheckupIdFromRoute: any;
  subscriptions: Array<Subscription> = [];
  faceChatMainConcern: string;
  faceChatSecondaryConcerns: string[];
  isShowHairlineDetectionExperiment: boolean;
  isHairlineDetected: boolean;
  isShowTempleBaldnessDetectionExperiment: boolean;
  isTempleBaldnessDetected: boolean;
  expandedItems: boolean[] = [];
  summaryFullText: string = '';
  summaryExpanded: boolean = false;
  responsiveCardReadMoreExperiment: boolean = false;
  handleBlurPhotosExperiment: boolean = false;
  noBlurDetected: boolean;
  blurWithConcernsDetected: boolean;
  blurWithoutConcernsDetected: boolean;
  currentUser: any;
  instantCheckups: any;
  availableImagesArray: any[];
  skipLocalStorageDelete: boolean = false;
  instantCheckupFromSupportChat: boolean = false;
  fromSelfCheckupBanner: boolean = false;
  today: Date = new Date();
  userCheckupImages: any;
  isReacquisitionUser: boolean = false;
  followUp: Table.FollowUp;
  excludeInstantCheckupProblems: string[] = [];
  private fupQuestionsSheetOpened: boolean = false;
  hairScalpDetectionExperiment: Table.Experiment & { variant: { showNoDetectionCard: boolean } };
  showNoScalpDetectionCard: boolean = false;
  isHairTopType: boolean = false;
  isHairFrontType: boolean = false;
  readonly hairAiDetectionTealColor = signal<boolean>(false);
  readonly showDetectionViewTab = signal<boolean>(false);
  scalpViewMode: WritableSignal<ScalpViewMode>;
  readonly scalpFilledPillRef = viewChild<ElementRef<HTMLElement>>('scalpFilledPill');
  readonly scalpOutlinedPillRef = viewChild<ElementRef<HTMLElement>>('scalpOutlinedPill');
  readonly scalpIndicatorLeft = signal<number>(0);
  readonly scalpIndicatorWidth = signal<number>(0);
  readonly SCALP_INDICATOR_TRANSITION_MS = 400;

  instantCheckupStatusBannerState: InstantCheckupStatusBannerStateEnum;
  InstantCheckupStatusBannerStateEnum = InstantCheckupStatusBannerStateEnum;
  isInvalidPhoto: boolean = false;
  isFollowUpReportPending: boolean = false;
  invalidPhotoErrorMappingExperiment: InvalidPhotoErrorMappingVariant | null = null;
  resolvedInvalidPhotoUiCode: InvalidPhotoUiCode = InvalidPhotoUiCode.FALLBACK;
  HeaderTypeEnum = HeaderTypeEnum;
  ButtonVariant = ButtonVariant;
  FooterContainerVariant = FooterContainerVariant;
  I18nKey = I18nKey;
  private _footerLayout: FooterLayout = { buttons: [], containerVariant: FooterContainerVariant.DEFAULT };
  private _footerLayoutDependencies: string = '';

  constructor(
    private localStorageService: LocalStorageService,
    private connectionService: ConnectionService,
    private appWebBridge: AppWebBridgeService,
    private eventLogger: EventLoggerService,
    public windowRef: WindowRefService,
    private dataStore: DataStoreService,
    public broadcast: BroadcastService,
    public route: ActivatedRoute,
    public appConfig: AppConfig,
    private router: Router,
    public bottomSheet: MatBottomSheet,
    public changeDetection: ChangeDetectorRef,
    private readonly instantCheckupService: InstantCheckupService,
    private readonly reAcquisitionService: ReAcquisitionService,
    private readonly currentComponent: CurrentComponentService,
    private readonly selfCheckupService: SelfCheckupService,
    private readonly followUpService: FollowUpService,
    private readonly experimentService: ExperimentService,
  ) {
    this.currentComponent.set(this);
    this.scalpViewMode = signal<ScalpViewMode>(this.appConfig.Shared.InstantCheckup.Detections.View.FILLED);
    effect((): void => {
      const mode = this.scalpViewMode();
      const filled = this.scalpFilledPillRef();
      const outlined = this.scalpOutlinedPillRef();
      if (!filled || !outlined) return;
      const View = this.appConfig.Shared.InstantCheckup.Detections.View;
      const target = mode === View.FILLED ? filled : outlined;
      const el = target.nativeElement;
      this.scalpIndicatorLeft.set(el.offsetLeft);
      this.scalpIndicatorWidth.set(el.offsetWidth);
    });
  }

  async ngOnInit(): Promise<void> {
    this.appWebBridge.setStatusBarColor('cureBlack');
    this.loading = true;
    this.isFromListView = this.route.snapshot.queryParams.fromList;
    this.instantCheckupFromSupportChat = this.route.snapshot?.queryParams.className === 'SupportChat';
    this.fromSelfCheckupBanner = this.route.snapshot.queryParams.fromSelfCheckupBanner || this.localStorageService.getValue('fromSelfCheckupBanner');

    this.experiments = await this.connectionService.findUserActiveExperiments();
    this.experiments?.forEach(async (experiment: any): Promise<any> => {
      if (experiment.key === 'show_hairline_detection') {
        this.isShowHairlineDetectionExperiment = true;
      }
      if (experiment.key === 'show_temple_baldness_detection') {
        this.isShowTempleBaldnessDetectionExperiment = true;
      }
      if (experiment.key === 'handle_blur_photos') {
        this.handleBlurPhotosExperiment = true;
      }
      if (experiment.key === 'responsive_card_read_more') {
        this.responsiveCardReadMoreExperiment = true;
      }
      if (experiment.key === 'exclude_instant_checkup_problems') {
        this.excludeInstantCheckupProblems = experiment?.variant || [];
      }
      if (experiment.key === 'hair_scalp_detection') {
        this.hairScalpDetectionExperiment = experiment;
      }
      if (experiment.key === 'invalid_photo_error_mapping') {
        this.invalidPhotoErrorMappingExperiment = experiment.variant;
      }
      if (experiment.key === 'hair_ai_detection_teal_color') {
        this.hairAiDetectionTealColor.set(true);
        this.showDetectionViewTab.set(!!experiment.variant?.showDetectionViewTab);
      }
    });
    await this.loadFollowUpReportStatus();

    this.user = this.connectionService.getActingUser();
    this.currentUser = this.connectionService.getActingUser();
    if (this.fromSelfCheckupBanner) {
      await this.getProgressReportClicked();
    }
    this.subscriptions.push(this.route.params.subscribe(async (param: any): Promise<void> => {
      this.instantCheckupIdFromRoute = param.id;
      await this.getInstantCheckup(this.instantCheckupIdFromRoute);
      this.isReacquisitionUser = this.getIsReacquisitionUser();
      if (this.isReacquisitionUser) this.reAcquisitionService.setIsPhotoTaken(true);
      this.validateAndRenderPhotoStatusBanner();
      this.loading = false;
    }));
  }

  private async loadFollowUpReportStatus(): Promise<void> {
    await this.followUpService.fetchPendingFollowups();
    const pendingFollowUp = this.followUpService.pendingFollowup();
    this.isFollowUpReportPending = pendingFollowUp?.get('State') === this.appConfig.Shared.Followup.State.PENDING
      && pendingFollowUp?.get('ready') === true;
  }

  /**
   * Handles back press when user presses back in Android camera UI.
   * Hides the loader if it's showing and lets navigation proceed.
   * This is called by app.component.ts when NAVIGATION_BACK is broadcast.
   */
  async handleBackPress(): Promise<boolean> {
    this.loading = false;
    this.currentComponent.remove(this);
    // Return false to let app.component.ts handle navigation
    return false;
  }

  getIsReacquisitionUser(): boolean {
    return this.instantCheckup?.extraData?.fromReacquisition === 'true' || this.route.snapshot.queryParams.fromReacquisition === 'true';
  }

  reorderResultsByConcerns(): AiDetectionResult[] {
    const moveMatchingResultsToFront = (concernKeys: string[], results: AiDetectionResult[]): AiDetectionResult[] => {
      const classesToMove = new Set<string>(
        concernKeys.flatMap((key: string): string[] => FACE_PROBLEM_CLASS_MAPPING[key] || []),
      );

      const [movedResults, remainingResults]: [AiDetectionResult[], AiDetectionResult[]] = results.reduce(
        ([moved, remaining]: [AiDetectionResult[], AiDetectionResult[]], result: AiDetectionResult): [AiDetectionResult[], AiDetectionResult[]] => {
          if (classesToMove.has(result.class)
            && !moved.some((r: AiDetectionResult): boolean => r.class === result.class && r.ProblemName === result.ProblemName)) {
            moved.push(result);
          } else {
            remaining.push(result);
          }
          return [moved, remaining];
        },
        [[], []],
      );

      return [...movedResults, ...remainingResults];
    };

    let reorderedResults: AiDetectionResult[] = [...this.results] as AiDetectionResult[];

    // eslint-disable-next-line angular/typecheck-array
    if (Array.isArray(this.faceChatSecondaryConcerns) && this.faceChatSecondaryConcerns.length > 0) {
      reorderedResults = moveMatchingResultsToFront(this.faceChatSecondaryConcerns, reorderedResults);
    }

    if (this.faceChatMainConcern && FACE_PROBLEM_CLASS_MAPPING[this.faceChatMainConcern]) {
      reorderedResults = moveMatchingResultsToFront([this.faceChatMainConcern], reorderedResults);
    }

    return reorderedResults;
  }

  /**
   * Fetch instant checkup and populate landmarks, image, detection etc.
   */
   
  async getInstantCheckup(idFromRoute: string): Promise<void> {
    [this.instantCheckup] = await this.connectionService.fetchInstantCheckup({ id: [idFromRoute] }) ?? [];
    this.isInvalidPhoto = this.instantCheckup.type === ApiClientConstant.InstantCheckup.Type.INVALID;
    this.resolveInvalidPhotoUiCode();
    this.consultationSession = await this.connectionService.findConsultationSession(this.appConfig.Shared.Regimen.Class.FACE);
    if (this.handleBlurPhotosExperiment) {
      this.checkForBlurryImage(this.instantCheckup);
    }
    this.image = await this.loadImage(this.instantCheckup?.imagePath);
    this.faceLandmarks = this.getLandmarks(this.instantCheckup);
    this.determineIsHairTopType();
    if (this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.HAIR_TOP && this.image) {
      // Set a default config immediately so detections can render
      // Will be updated with correct values once canvas is available
      const defaultScale = Math.min(
        (this.windowRef.nativeWindow.innerWidth * 0.9) / this.image.width,
        (this.windowRef.nativeWindow.innerHeight * 0.6) / this.image.height
      );
      this.croppingConfig = { left: 0, top: 0, ratio: defaultScale, offsetX: 0, offsetY: 0 };
    }

    this.changeDetection.detectChanges();
    // Disable concerns for help trees

    // Add logic here to send the event for invalid photos
    if (this.instantCheckup.type === this.appConfig.Shared.InstantCheckup.Type.INVALID) {
      this.eventLogger.cleverTapEvent('face_not_found', JSON.stringify({ pageName: 'InstantCheckup' }), [
        this.appConfig.Shared.EventTracker.CLEVER_TAP,
        this.appConfig.Shared.EventTracker.MOENGAGE,
        this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
      ]);
    }

    if (this.instantCheckup?.extraData?.className === 'SupportChat' || this.instantCheckup?.extraData?.supportTicketId) {
      this.isHairTopType = false;
      this.showNoScalpDetectionCard = false;
      return;
    }

    if (['LEFT_SIDE_FACE', 'FRONT_FACE', 'RIGHT_SIDE_FACE'].includes(this.instantCheckup?.type)) {
      const results = [...(this.instantCheckup?.aiResponse?.result || [])];
      const mainConcern = await this.consultationSession?.get('PrivateMainConcern');

      this.results = [...results
        .filter((result: AiDetectionResult): boolean => result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED)
        .filter((result: AiDetectionResult): boolean =>
          !result.showForGender ||
          this.user?.get('Gender')?.toLowerCase() === result.showForGender?.toLowerCase()
        )
        .filter((result: AiDetectionResult): boolean => !this.excludeInstantCheckupProblems?.includes(result.class))
        .filter((result: AiDetectionResult): boolean =>
          result.class !== 'Melasma' ||
          (
            result.class === 'Melasma' &&
            !['LEFT_SIDE_FACE', 'RIGHT_SIDE_FACE'].includes(this.instantCheckup.type) &&
            (
              (mainConcern && ['pigmentation', 'melasma'].includes(mainConcern.toLowerCase()))
              || (result.Severity && result.Severity?.toLowerCase() !== 'mild')
            )
          )
        ),
      ];

      try {
        /*
         TODO: Instead of using FACE to get the consultation session, we will use the user selected regimen class
         once we have the feature to show analysis result for HAIR as well so that it works for both FACE and HAIR.
       */
        this.faceChatMainConcern = await this.consultationSession?.get('PrivateMainConcern');
        this.faceChatSecondaryConcerns = await this.consultationSession?.get('concernList');
        this.results = this.reorderResultsByConcerns();
      } catch (err) {
        // handle error
      }

      // check severity
      this.results = (this.results as AiDetectionResult[]).map((result: AiDetectionResult): AiDetectionResult => {
        const severity = this.instantCheckupService.getSeverityFromConcern(result);
        return { ...result, severity: typeof severity === 'string' ? severity : undefined };
      });
      this.summaryFullText = (this.results as AiDetectionResult[])
        .map((concern: AiDetectionResult): string => this.problemsCopy[this.user?.get('languagePreference') || 'en'][concern.class]?.name)
        .join(', ');
      this.loading = false;
    } else if (['HAIR_FRONT'].includes(this.instantCheckup?.type) && this.instantCheckup?.aiResponse?.result?.length) {
      this.results = [];

      const results = [...(this.instantCheckup?.aiResponse?.result ?? [])]
        .filter((result: AiDetectionResult): boolean => result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED)
        .filter((result: AiDetectionResult): boolean =>
          !result.showForGender ||
          this.user?.get('Gender')?.toLowerCase() === result.showForGender?.toLowerCase()
        );

      const baldness = results.find(result => result?.['class'] === this.appConfig.Shared.InstantCheckup.Concerns.TEMPLE_BALDNESS);
      const hairline = results.find(result => result?.['class'] === this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE);

      if (this.isShowTempleBaldnessDetectionExperiment && baldness) {
        this.isTempleBaldnessDetected = true;
        this.results.push({
          class: this.appConfig.Shared.InstantCheckup.Concerns.TEMPLE_BALDNESS,
          PolygonPointsSequences: [
            [...(baldness?.ellipse_points?.left ?? [])],
            [...(baldness?.ellipse_points?.right ?? [])],
          ],
        });
      }
      if (this.isShowHairlineDetectionExperiment && hairline) {
        this.isHairlineDetected = true;
        this.results.push({
          class: this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE,
          PolygonPointsSequences: [
            { color: '#ffffff', points: (hairline?.hairline_points ?? []) },
          ],
        });
      }

    } else {
      this.results = [];

      if (this.isShowTempleBaldnessDetectionExperiment
        && (this.instantCheckup.aiResponse?.ellipse_points?.left?.length
          || this.instantCheckup.aiResponse?.ellipse_points?.right?.length)) {
        this.isTempleBaldnessDetected = true;
        this.results.push({
          class: this.appConfig.Shared.InstantCheckup.Concerns.TEMPLE_BALDNESS,
          PolygonPointsSequences: [
            [...(this.instantCheckup.aiResponse?.ellipse_points?.left || [])],
            [...(this.instantCheckup.aiResponse?.ellipse_points?.right || [])],
          ],
        });
      }
      if (this.isShowHairlineDetectionExperiment && this.instantCheckup.aiResponse?.points_data?.hairline_points?.length) {
        this.isHairlineDetected = true;
        this.results.push({
          class: this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE,
          PolygonPointsSequences: [
            { color: '#ffffff', points: (this.instantCheckup.aiResponse?.points_data?.hairline_points || []) },
          ],
        });
      }
      if (this.isHairTopType) {
        this.setDefaultHairTopCroppingConfig();
        this.updateHairTopCroppingConfig();
        this.processScalpDetections();
      }
      this.summaryFullText = this.results
        .map((concern: DisplayDetectionResult): string => this.problemsCopy[this.user?.get('languagePreference') || 'en'][concern.class ?? '']?.name)
        .join(', ');
      this.loading = false;
    }

    if (this.hairAiDetectionTealColor() && !this.showDetectionViewTab() && this.isHairTopType) {
      this.applyTealColorToHairDetections();
    }

    // Move this to before the return statement of support chat
    if (this.instantCheckup?.type === this.appConfig.Shared.InstantCheckup.Type.INVALID) {
      this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
        branch: { name: 'instantCheckupFaceNotDetected' },
        firebase: { name: 'instantCheckupFaceNotDetected' },
      });
    }

    this.tag = this.instantCheckup?.originalType || this.instantCheckup?.type;
    if (this.tag === this.appConfig.Shared.InstantCheckup.Type.INVALID) {
      this.tag = this.appConfig.Shared.InstantCheckup.Type.FULL_FACE;
    }

    if (!this.isFromListView && (<string> this.instantCheckup.type).includes('FACE')) {
      const detections = (this.results as AiDetectionResult[]).reduce(
        (prev: Record<string, number>, curr: AiDetectionResult): Record<string, number> => {
          const detectionCount = curr.BoundingBoxes?.length || curr.PolygonPointsSequences?.length || 0;
          const binaryDetectionType = ['Melasma', 'DarkCircle', 'OpenPores'];
          return { ...prev, [curr.class]: binaryDetectionType.includes(curr.class) ? 1 : detectionCount };
        },
        {},
      );

      this.eventLogger.cleverTapEvent('face_concern_detections', JSON.stringify({
        type: this.instantCheckup.type,
        ...detections,
        total_count: this.results?.length || 0,
      }), PRIMARY_CHANNELS);
    }

    if (!this.isFromListView && (<string> this.instantCheckup.type).includes('HAIR')) {
      const baldnessEllipsePoints = [...(this.instantCheckup?.aiResponse?.result ?? [])]
        .filter((result: AiDetectionResult): boolean => result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED)
        .filter((result: AiDetectionResult): boolean =>
          !result.showForGender ||
          this.user?.get('Gender')?.toLowerCase() === result.showForGender?.toLowerCase()
        ).find((result: AiDetectionResult): boolean => result?.['class'] === this.appConfig.Shared.InstantCheckup.Concerns.TEMPLE_BALDNESS)?.ellipse_points
        ?? this.instantCheckup.aiResponse?.ellipse_points;

        this.eventLogger.cleverTapEvent('hair_concern_detections', JSON.stringify({
        type: this.instantCheckup.type,
        hairline: this.isHairlineDetected ? 1 : 0,
        temple_baldness: Object.keys(baldnessEllipsePoints ?? {})?.length || 0,
        total_count: this.results.reduce(
          (sum: number, result: DisplayDetectionResult): number => sum + (result.PolygonPointsSequences?.length || 0),
          0,
        ),
      }));
    }
    this.loading = false;
  }

  validateAndRenderPhotoStatusBanner(): void {
    const isHairTop = this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.HAIR_TOP;
    if (!this.instantCheckup) return;
    if (!isHairTop) return;

    const refreshDate = this.user.get('refreshDate');
    const orderState = this.user.get('orderState') || ApiClientConstant.User.OrderState.UNPAID;
    if (this.instantCheckup.createdAt <= refreshDate) {
      this.instantCheckupStatusBannerState = InstantCheckupStatusBannerStateEnum.PHOTO_REVIEWED;
    } else if ([ApiClientConstant.User.OrderState.UNPAID, ApiClientConstant.User.OrderState.NEW_USER].includes(orderState)) {
      this.instantCheckupStatusBannerState = InstantCheckupStatusBannerStateEnum.PHOTO_WILL_BE_REVIEWED;
    } else {
      this.instantCheckupStatusBannerState = InstantCheckupStatusBannerStateEnum.PHOTO_REVIEW_PENDING;
    }
  }

  private resolveInvalidPhotoUiCode(): void {
    if (!this.invalidPhotoErrorMappingExperiment ||
      !this.isInvalidPhoto) return;

    const errorMessage = this.instantCheckup?.errorMessage;
    if (!errorMessage) return;

    const mappings = this.invalidPhotoErrorMappingExperiment.errorMappings || [];
    const matched = mappings.find((m: InvalidPhotoErrorMapping): boolean => m.errorMessage === errorMessage);
    if (!matched) return;

    const categoryKey = UI_CODE_CATEGORY_MAP[matched.uiCode];
    if (!categoryKey ||
      !this.invalidPhotoErrorMappingExperiment[categoryKey]) return;

    this.resolvedInvalidPhotoUiCode = matched.uiCode as InvalidPhotoUiCode;
  }

  checkForBlurryImage(checkup: any): void {
    this.noBlurDetected = false;
    this.blurWithConcernsDetected = false;
    this.blurWithoutConcernsDetected = false;



    const isFrontFace: boolean = checkup?.type === this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE
      && this.localStorageService.getValue('expectedFacePhoto') === this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE;
    if (!isFrontFace) return;

    const aiResponse = checkup?.aiResponse;
    const isMildMelasma = (c: AiDetectionResult): boolean => c.class !== 'Melasma' || (c.class === 'Melasma' && c.Severity?.toLowerCase() !== 'mild');
    const result: AiDetectionResult[] = aiResponse?.result.filter((result: AiDetectionResult): boolean => isMildMelasma(result));
    const hasConcerns = result.some((concern: AiDetectionResult): boolean => concern?.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED);
    const isBlurry: boolean = aiResponse?.blurClassLabel === this.appConfig.Shared.InstantCheckup.AiResponse.BLURRED;

    // No blur
    if (!isBlurry) {
      this.noBlurDetected = true;
      this.triggerCustomEvent('no_blur');
      return;
    }
    // Blur + concerns
    if (hasConcerns) {
      this.blurWithConcernsDetected = true;
      this.triggerCustomEvent('blur_with_concerns');
      return;
    }
    // Blur + no-concern
    this.blurWithoutConcernsDetected = true;
    this.triggerCustomEvent('blur_without_concerns');
  }

  triggerCustomEvent(eventName: string): void {
    this.eventLogger.cleverTapEvent(`${eventName}`, JSON.stringify({ pageName: 'InstantCheckup' }));
  }

  getLandmarks(instantCheckup: any): { top: Point, left: Point, right: Point, bottom: Point, center: Point, faceBox: any } {
     
    if (['LEFT_SIDE_FACE', 'FRONT_FACE', 'RIGHT_SIDE_FACE'].includes(instantCheckup?.type)) {
      const landmarks = this.instantCheckup?.aiResponse?.googleResponse?.faceAnnotations?.[0]?.landmarks;
      const distanceBetweenChinAndNose = (landmarks?.CHIN_GNATHION.y || 0) - (landmarks?.NOSE_TIP.y || 0);
      return {
        top: {
          x: landmarks?.FOREHEAD_GLABELLA?.x,
          y: (landmarks?.FOREHEAD_GLABELLA?.y || 0)
            - distanceBetweenChinAndNose,
        },
        left: landmarks?.LEFT_EAR_TRAGION,
        bottom: landmarks?.CHIN_GNATHION,
        right: landmarks?.RIGHT_EAR_TRAGION,
        center: landmarks?.NOSE_TIP,
        faceBox: this.instantCheckup?.aiResponse?.faceBox,
      };
    }

    const landmarks = this.instantCheckup?.aiResponse?.fb_landmarks ?? this.instantCheckup?.aiResponse?.result?.[0]?.fb_landmarks;
    const distanceBetweenChinAndNose = (landmarks?.CHIN_GNATHION?.[1] || 0)
      - (landmarks?.NOSE_TIP?.[1] || 0);
    return {
      top: {
        x: landmarks?.FOREHEAD_GLABELLA?.[0],
        y: (landmarks?.FOREHEAD_GLABELLA?.[1] || 0)
          - distanceBetweenChinAndNose,
      },
      left: {
        x: landmarks?.LEFT_EAR_TRAGION?.[0],
        y: landmarks?.LEFT_EAR_TRAGION?.[1],
      },
      bottom: {
        x: landmarks?.CHIN_GNATHION?.[0],
        y: landmarks?.CHIN_GNATHION?.[1],
      },
      right: {
        x: landmarks?.RIGHT_EAR_TRAGION?.[0],
        y: landmarks?.RIGHT_EAR_TRAGION?.[1],
      },
      center: {
        x: landmarks?.NOSE_TIP?.[0],
        y: landmarks?.NOSE_TIP?.[1],
      },
      faceBox: null,
    };
  }

  /**
   * Select active skin detection
   * @param detection current active detection
   */
  redrawScaledCanvas(zoomBox?: HTMLDivElement): void {
    const overaly = this.windowRef.nativeWindow.document.getElementById('overlayCanvas');
    if (overaly) overaly.parentElement.removeChild(overaly);
    if (zoomBox) zoomBox.removeAttribute('style');
    if (zoomBox) this.findScaling(zoomBox);
  }

  slide(swiper?: SwiperComponent): void {
    const swiperRef = swiper ? swiper.swiperRef : this.swiper?.swiperRef;
    this.readMoreComponents?.forEach((component: ReadMoreComponent): any => component.closeText());
    this.summaryExpanded = false;
    this.expandedItems.fill(false);
    if (!swiperRef) return;

    if (!this.selectedDetection) {
      swiperRef.slideTo(0);
      return;
    }
    const index = this.results.indexOf(this.selectedDetection) + 1;
    swiperRef.slideTo(index);
  }

  selectDetection(event: any): void {
    const [swiperEvent]: any = event;
    if (swiperEvent.activeIndex === 0) {
      this.selectedDetection = null;
    } else {
      this.selectedDetection = this.results[swiperEvent.activeIndex - 1];
    }
  }

  findScaling(zoomBox: HTMLDivElement): void {
    const zoomBoxWidth = zoomBox.offsetWidth;
    const zoomBoxHeight = zoomBox.offsetHeight;
    const zoomBoxAspectRatio = zoomBoxWidth / zoomBoxHeight;
    const zoomBoxCenter = {
      x: zoomBoxWidth / 2,
      y: zoomBoxHeight / 2,
    };
    let points = { minX: Number.MAX_VALUE, minY: Number.MAX_VALUE, maxX: Number.MIN_VALUE, maxY: Number.MIN_VALUE };
    if (this.selectedDetection.BoundingBoxes) points = this.findBoundingBox(this.selectedDetection.BoundingBoxes);
    if (this.selectedDetection.PolygonPointsSequences) points = this.findBoundingPolygon(this.selectedDetection.PolygonPointsSequences as unknown as string[][]);

    points = {
      minX: (points.minX - this.croppingConfig.left) * this.croppingConfig.ratio,
      minY: (points.minY - this.croppingConfig.top) * this.croppingConfig.ratio,
      maxX: (points.maxX - this.croppingConfig.left) * this.croppingConfig.ratio,
      maxY: (points.maxY - this.croppingConfig.top) * this.croppingConfig.ratio,
    };

    let boundingBoxWidth = points.maxX - points.minX;
    let boundingBoxHeight = points.maxY - points.minY;
    const boundingBoxCenter = {
      x: points.minX + boundingBoxWidth / 2,
      y: points.minY + boundingBoxHeight / 2,
    };

    // draw overlay here
    this.drawOverlay(zoomBox, { x: points.minX, y: points.minY }, boundingBoxWidth, boundingBoxHeight);
    // -----------------

    boundingBoxHeight += 100;
    boundingBoxWidth += 100;

    const boundigBoxAspectRatio = boundingBoxWidth / boundingBoxHeight;
    if (boundigBoxAspectRatio > zoomBoxAspectRatio) {
      boundingBoxHeight = (zoomBoxHeight / zoomBoxWidth) * boundingBoxWidth;
    }
    const scale = Math.min((zoomBoxHeight / boundingBoxHeight), 2.5);
    if (scale <= 1) return;

    const x = (zoomBoxCenter.x - boundingBoxCenter.x);
    const y = (zoomBoxCenter.y - boundingBoxCenter.y);

    const maxTranslateX = (zoomBox.clientWidth * scale - zoomBox.clientWidth) / 2;
    const minTranslateX = -(zoomBox.clientWidth * scale - zoomBox.clientWidth) / 2;
    const maxTranslateY = (zoomBox.clientHeight * scale - zoomBox.clientHeight) / 2;
    const minTranslateY = -(zoomBox.clientHeight * scale - zoomBox.clientHeight) / 2;

    const translate = {
      x: Math.sign(x * scale) === 1 ? Math.min(x * scale, maxTranslateX) : Math.max(x * scale, minTranslateX),
      y: Math.sign(y * scale) === 1 ? Math.min(y * scale, maxTranslateY) : Math.max(y * scale, minTranslateY),
    };

    // zoomBox.style.setProperty('transform-origin', `${origin.x}px ${origin.y}px`);
    zoomBox.style.setProperty('translate', `${translate.x}px ${translate.y}px`);
    zoomBox.style.setProperty('transform', `scale(${scale})`);
  }

  findBoundingBox(param: string[]): { minX: number, minY: number, maxX: number, maxY: number } {
    let points = { minX: Number.MAX_VALUE, minY: Number.MAX_VALUE, maxX: Number.MIN_VALUE, maxY: Number.MIN_VALUE };
    param.forEach((pointString: string): void => {
      const pointArray = pointString.split(',').map((x: string): number => +x);
      points = {
        minX: Math.min(pointArray[0], pointArray[2], points.minX),
        minY: Math.min(pointArray[1], pointArray[3], points.minY),
        maxX: Math.max(pointArray[0], pointArray[2], points.maxX),
        maxY: Math.max(pointArray[1], pointArray[3], points.maxY),
      };
    });
    return points;
  }
  findBoundingPolygon(polygons: string[][]): { minX: number, minY: number, maxX: number, maxY: number } {
    let points = { minX: Number.MAX_VALUE, minY: Number.MAX_VALUE, maxX: Number.MIN_VALUE, maxY: Number.MIN_VALUE };
    polygons.forEach((polygon: string[]): void => {
      polygon.forEach((pointString: string): void => {
        const pointArray = pointString.split(',').map((x: string): number => +x);
        points = {
          minX: Math.min(pointArray[0], points.minX),
          minY: Math.min(pointArray[1], points.minY),
          maxX: Math.max(pointArray[0], points.maxX),
          maxY: Math.max(pointArray[1], points.maxY),
        };
      });
    });
    return points;
  }

  drawOverlay(zoomBox: HTMLDivElement, point: { x: number, y: number }, width: number, height: number): void {
    const canvas = this.windowRef.nativeWindow.document.createElement('canvas');
    const canvasContext = canvas.getContext('2d');
    canvas.height = zoomBox.clientHeight;
    canvas.width = zoomBox.clientWidth;
    canvas.id = 'overlayCanvas';
    canvas.style.setProperty('position', 'absolute');
    canvas.style.setProperty('top', '0');
    canvas.style.setProperty('left', '0');
    canvasContext.fillStyle = 'rgba(0, 0, 0, 0.5)';
    canvasContext.fillRect(0, 0, canvas.width, canvas.height);
    canvasContext.stroke();
    canvasContext.beginPath(); // Start a new path
    canvasContext.clearRect(point.x - 10, point.y - 10, width + 20, height + 20);
    canvasContext.rect(point.x - 10, point.y - 10, width + 20, height + 20);
    canvasContext.lineWidth = 3;
    canvasContext.strokeStyle = 'rgba(255, 255, 255, 0.65)';
    canvasContext.stroke();

    zoomBox.appendChild(canvas);
  }

  deleteInstantCheckup(confirmDelete?: boolean): void {
    if (this.ui.deleteLoading || !this.instantCheckup) return;
    this.ui.deleteLoading = true;
    this.connectionService.deleteInstantCheckup(this.instantCheckup.objectId, confirmDelete)
      .then((): void => {
        this.notify('Deleted Successfully');
        this.navigateAfterDelete();
        this.ui.deleteLoading = false;
      })
      .catch((err: any): void => {
        if (err.code === 409) {
          this.bottomSheet.open(DeletePhotoSheetComponent, {
            data: {
              content: err.message || err,
              clickNewPhotoCallback: (): void => {
                this.goCheckAgain();
              },
              deleteCallback: (): void => {
                this.deleteInstantCheckup(true);
              },
            },
          });
        } else {
          const message = err.message || err;
          this.ui.popUpModal = {
            title: this.appConfig.Shared.String.DELETE_PHOTO,
            open: true,
            okText: this.appConfig.Shared.String.DELETE,
            cancelText: this.appConfig.Shared.String.KEEP_PHOTO,
            message: { text: message },
            type: this.appConfig.Dialog.ALERT,
          };
        }
        this.ui.deleteLoading = false;
      });
  }

  private get shouldOpenMoreInsightsSheetOnBack(): boolean {
    if (!this.fromSelfCheckupBanner) return false;
    if (this.localStorageService.getBooleanValue('moreInsightsBottomsheetShown')) return false;
    const newUiExperimentActive = this.experimentService.isFupQuestionsNewUiEnabled();
    return !this.followUpService.isFirstFollowUp(this.followUp) || !newUiExperimentActive;
  }

  back(step?: number): void {
    if (this.shouldOpenMoreInsightsSheetOnBack) {
      const data = { fromSelfCheckupBanner: this.fromSelfCheckupBanner, followUpId: this.followUp?.id };
      const sheetRef: MatBottomSheetRef<MoreInsightsBottomsheetComponent> = this.bottomSheet.open(MoreInsightsBottomsheetComponent, {data});
      sheetRef.backdropClick().pipe(take(1)).subscribe(() => sheetRef.dismiss());
      this.localStorageService.setValue('moreInsightsBottomsheetShown', true);
      return;
    }

    const cameFromSelfCheckupBanner = this.fromSelfCheckupBanner;
    this.deleteLocalStorageItem('fromSelfCheckupBanner');
    this.deleteLocalStorageItem('moreInsightsBottomsheetShown');
    if (cameFromSelfCheckupBanner) {
      this.navigateToHome();
      return;
    }
    this.broadcast.broadcast('NAVIGATION_BACK', { step });
  }

  navigateToHome(): void {
    this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  private navigateAfterDelete(): void {
    const data = this.appWebBridge.isAppWebBridgeLoaded()
      ? this.instantCheckup?.extraData
      : this.route.snapshot.queryParams;

    const { CONSULTATION_SESSION_CHAT } = this.appConfig.Shared.CLASSNAME;

    if (data?.className === CONSULTATION_SESSION_CHAT || data?.consultationSessionId) {
      this.router.navigate([`/chatV2/${data.consultationSessionId}`], { queryParams: { type: 'consultationSession' } });
      return;
    }

    if (data?.followUpId) {
      this.router.navigate([`/chatV2/${data.followUpId}`], { queryParams: { type: 'followUp' } });
      return;
    }

    if (this.fromSelfCheckupBanner) {
      this.navigateToHome();
      this.deleteLocalStorageItem('fromSelfCheckupBanner');
      return;
    }

    this.back();
  }

  /**
   * Load an image in promise
   * @param src image src
   * @returns HTMLImageElement
   */
  async loadImage(src: string): Promise<HTMLImageElement> {
    if (!src) return new Image();
    return new Promise((resolve: any, reject: any): void => {
      const image = new Image();
      image.src = src;
      image.onload = (): void => resolve(image);
      image.onerror = (): void => {
        reject(new Error('Cant process image'));
      };
    });
  }

  popUpClosed(result: any): void {
    this.ui.popUpModal = { open: false };
    if (result.clickOnYes) {
      this.deleteInstantCheckup(true);
    }
  }

  notify(message: any): void {
    this.broadcast.broadcast('NOTIFY', { message });
  }

  checkAndOpenIncorrectFaceDetectedBottomSheet(): void {
    const expectedPhotoType = this.localStorageService?.getValue('expectedFacePhoto');
    if (!expectedPhotoType) {
      return;
    }
    const instantCheckupPhotoType = this.instantCheckup.type;
    this.doesPhotoTypeMatches = instantCheckupPhotoType === expectedPhotoType || instantCheckupPhotoType.includes(expectedPhotoType);
    this.shouldShowIncorrectPhotoDetectedSheet = (instantCheckupPhotoType.includes(this.appConfig.Shared.InstantCheckup.Type.FACE)
      && !this.doesPhotoTypeMatches);
    if (this.shouldShowIncorrectPhotoDetectedSheet) {
      this.bottomSheet.open(WrongPhotoDetectedComponent, {
        data: {
          photoTypes: { expectedPhoto: expectedPhotoType, actualPhoto: instantCheckupPhotoType },
          retakePhotoFromBottomSheet: (): void => {
            this.handleImageCapture();
          },
        },
      });
    }
  }

  handleImageCapture(): void {
    let extraDataFromLocalStorage;
    const rawLocalStorageData = this.localStorageService.getValue('instantCheckupExtraData');
    if (rawLocalStorageData) {
      extraDataFromLocalStorage = JSON.parse(rawLocalStorageData);
    }
    const expectedPhotoType = this.localStorageService.getValue('expectedFacePhoto')
    || this.localStorageService.getValue('checkupListExpectedFacePhoto') || this.tag;
    if (this.appWebBridge.isAppWebBridgeLoaded()) {
      this.appWebBridge.notifyWebLoginToken();
      const extraData = Object.keys(this.route.snapshot.queryParams ?? {}).length > 0
        ? { ...this.route.snapshot.queryParams }
        : { ...extraDataFromLocalStorage };
      delete extraData.tag;
      this.appWebBridge.notifyCaptureImage(expectedPhotoType, {}, extraData);
    } else if (this.cameraInput) {
      this.cameraInput.nativeElement.click();
    } else {
      this.windowRef.nativeWindow.document.getElementById('cameraInput').click();
    }
  }

  private async checkForMissingFacesAndPrompt(): Promise<boolean> {
    const { Shared: { InstantCheckup, Followup }, }: AppConfig = this.appConfig;
    const frontFaceType = InstantCheckup.Type.FRONT_FACE;
    const leftSideType = InstantCheckup.Type.LEFT_SIDE_FACE;
    const rightSideType = InstantCheckup.Type.RIGHT_SIDE_FACE;
    const sideFaceType = InstantCheckup.Type.SIDE_FACE;
    const skipState = Followup.State.SKIP;

    const shouldSkipSideFaceCheck = (
      this.isReacquisitionUser ||
      this.instantCheckupFromSupportChat ||
      this.instantCheckupService.isSideFaceNudgeShownInSession() ||
      this.localStorageService.getBooleanValue('sideFaceUploadSkipped')
    );
    if (shouldSkipSideFaceCheck) return true;

    const isFrontFace: boolean = this.instantCheckup?.type === frontFaceType
      || this.localStorageService.getValue('expectedFacePhoto') === frontFaceType;

    if (isFrontFace) return true;
    const allInstantCheckups = await this.connectionService.fetchInstantCheckup({ userId: this.currentUser?.id });

    const hasLeftSide = allInstantCheckups?.some((c: any): boolean => c.type === leftSideType);
    const hasRightSide = allInstantCheckups?.some((c: any): boolean => c.type === rightSideType);

    if (hasLeftSide && hasRightSide) return true;

    const availableImagesArray: { image: string; side: string }[] = [];
    let requiredSides = [leftSideType, rightSideType];
    const instantCheckupTypeCreated: string = `${this.instantCheckup?.type?.split('_')[1]}_${this.instantCheckup?.type?.split('_')[2]}`;

    if (this.instantCheckup?.type && (this.instantCheckup?.aiOriginalType === sideFaceType || instantCheckupTypeCreated === sideFaceType)) {
      availableImagesArray.push({
        image: this.instantCheckup.imagePath,
        side: this.instantCheckup.type,
      });
      requiredSides = requiredSides.filter((side: string): boolean => side !== this.instantCheckup.type);
    }
    if (availableImagesArray.length === 2) {
      const leftIndex: number = availableImagesArray.findIndex(
        (item: { image: string; side: string }): boolean => item.side === leftSideType,
      );
      const rightIndex: number = availableImagesArray.findIndex(
        (item: { image: string; side: string }): boolean => item.side === rightSideType,
      );
      if (leftIndex !== -1 && rightIndex !== -1) {
        const leftItem: { image: string; side: string } = availableImagesArray[leftIndex];
        const rightItem: { image: string; side: string } = availableImagesArray[rightIndex];
        availableImagesArray[0] = leftItem;
        availableImagesArray[1] = rightItem;
      }
    }
    this.availableImagesArray = availableImagesArray;
    if (requiredSides.length > 0 && availableImagesArray.length === 1) {
      const bottomSheetRef: any = this.bottomSheet.open(BothSideFaceBottomSheetComponent, {
        data: {
          availableImages: availableImagesArray,
          requiredSide: requiredSides[0],
          callback: async (skipLocalStorageDelete: boolean): Promise<void> => {
            this.skipLocalStorageDelete = skipLocalStorageDelete;
            await this.goCheckAgain();
          },
        },
        disableClose: true,
      });
      bottomSheetRef.backdropClick().subscribe((): void => {
        this.localStorageService.setValue('sideFaceUploadSkipped', true)
        bottomSheetRef.dismiss(skipState);
      });
      const userAction = await bottomSheetRef.afterDismissed().toPromise();
      return userAction === skipState;
    }
    return true;
  }

  private isFromConsultationOrFollowupFlow(): boolean {
    const data = this.appWebBridge.isAppWebBridgeLoaded()
      ? this.instantCheckup?.extraData
      : this.route.snapshot.queryParams;
    const { CONSULTATION_SESSION_CHAT, FOLLOW_UP_CHAT } = this.appConfig.Shared.CLASSNAME;
    const isFromSelfCaptureBanner = data?.fromSelfCaptureBanner === true || data?.fromSelfCaptureBanner === 'true';
    return (
      isFromSelfCaptureBanner || !!data?.fromSelfCheckupBanner ||
      data?.className === CONSULTATION_SESSION_CHAT ||
      data?.className === FOLLOW_UP_CHAT ||
      !!data?.consultationSessionId ||
      !!data?.followUpId
    );
  }

  /**
   * This function decides where to take the user to when they click 'continue' button in footer.
   * 1. if no main-concern, then it navigates user to either mainconcern selection page or photo taking page based on experiment.
   * 2. If there is any redirect url set it local-storage, it takes them to there.
   * 3. Else to chat page.
   */
  async continueToNext(): Promise<any> {
    const queryParams = this.route.snapshot.queryParams;
    const sideFaceSheetResult = await this.instantCheckupService.validateAndOpenSideFacePhotoBottomSheet({
      currentInstantCheckup: this.instantCheckup as InstantCheckup,
      isReacquisitionUser: this.isReacquisitionUser,
      isConsultationOrFollowup: this.isFromConsultationOrFollowupFlow(),
      userId: this.currentUser?.id,
      callback: async () => {
        const { extraData, messageId, tag, ...remainingQueryParams } = queryParams;
        await this.instantCheckupService.navigateToInstantCheckupPreview({ ...remainingQueryParams, tag: ApiClientConstant.InstantCheckup.Type.SIDE_FACE});
      }
    });
    if (sideFaceSheetResult === SideFacePhotoBottomSheetComponent.DISMISS_UPLOAD) {
      return null;
    }
    const canProceed: boolean = await this.checkForMissingFacesAndPrompt();
    if (!canProceed) {
      return null;
    }
    this.checkAndOpenIncorrectFaceDetectedBottomSheet();
    const openReacquisitionUserBottomSheets = await this.shouldOpenReacquisitionUserBottomSheets();
    if (openReacquisitionUserBottomSheets) {
      return null;
    }
    if (this.shouldShowIncorrectPhotoDetectedSheet) return null;
    const isFromTakePhotoBanner = this.localStorageService.getBooleanValue('takePhotoBannerClicked');
    if (isFromTakePhotoBanner) {
      this.eventLogger.cleverTapEvent('photo_taken_upload_photo_banner');
      this.localStorageService.delete('takePhotoBannerClicked');
    }
    this.localStorageService.delete('instantCheckupExtraData');
    const redirectTo = this.localStorageService.getValue('CureSkin/redirectUrl');
    if (redirectTo) return this.connectionService.redirectToLastKnowLocation();
    this.eventLogger.trackEvent('continue_to_chat_from_instant_checkup', { username: this.user.get('username') });
    if (this.user.get('followupTreeTriggered')) {
      const followUp = await this.connectionService.findRecentFollowUp({
        State: [
          this.appConfig.Shared.Followup.State.PENDING,
          this.appConfig.Shared.Followup.State.WAITING_FOR_IMAGE,
        ],
        ready: false,
      });
      return this.router.navigate([`/chatV2/${followUp?.id}`], {
        queryParams: { type: 'followUp' },
      });
    }
    const routeString = await this.checkForRegimenAndNavigate();
    if (routeString) {
      return this.router.navigate(['/user'], { queryParams: { tab: 'regimen', class: routeString } });
    }
    return this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  async checkForRegimenAndNavigate(): Promise<string> {
    const regimenClasses = [this.appConfig.Shared.Regimen.Class.FACE, this.appConfig.Shared.Regimen.Class.HAIR];
    const regimens = await this.connectionService.fetchRegimens(null, true);

    const foundClass = regimenClasses.find((regimenClass: any): boolean => {
      const includesTag = this.tag.includes(regimenClass);
      return includesTag && !this.hasPaidRegimen(regimens, regimenClass);
    });

    return foundClass || '';
  }

  hasPaidRegimen(regimens: Array<AllocatedRegimen>, regimenClass: string): boolean {
    return regimens.some((regimen: AllocatedRegimen): boolean => regimen?.active && regimen?.class === regimenClass);
  }

  /**
  * When Check again button is clicked.
  * 1. Calls native to open camera when its inside native app.
  * 2. Open Native camera through web when experiment is enabled.
  * 3. Routes to capture page, which is a web-cam(video) based capturing experience. We capture the image of video.
  */
  async goCheckAgain(): Promise<any> {
    this.loading = true;
    this.skipLocalStorageDelete = true;
    this.eventLogger.trackEvent('check_again', { username: this.user.get('username'), type: this.tag });
    this.eventLogger.trackPeopleIncrement({ people_take_photo_clicked: 1 });
    this.handleImageCapture();
  }

  /**
   * Callback of native camera picture upload. i.e Native camera opened through web, not through app bridge.
   */
  async uploadImageFromNativeCamera(event: any): Promise<any> {
    const nativeCameraImage = event.target.files[0];
    await this.dataStore.set('IMAGE_FILE', { file: nativeCameraImage });
    const extraData = { ...(this.route.snapshot.queryParams || {}) };
    this.router.navigate(['/user/instantCheckup/capture'],
      { queryParams: { tag: this.tag, nativeCameraFile: true, ...extraData }, replaceUrl: true });
  }

  ngOnDestroy(): void {
    this.currentComponent.remove(this);
    this.subscriptions.map((each: Subscription): void => each.unsubscribe());
    this.subscriptions = [];
    if (!this.skipLocalStorageDelete) {
      this.deleteLocalStorageItem('expectedFacePhoto');
    }
    this.deleteLocalStorageItem('checkupListExpectedFacePhoto');
  }

  deleteLocalStorageItem(field: string): void {
    this.localStorageService.delete(field);
  }

  async getProgressReportClicked(): Promise<boolean | void> {
    const result = await this.selfCheckupService.resolveProgressFollowUp({ markReady: true });
    if (result) {
      this.followUp = result.followUp;
      if (this.experimentService.isFupQuestionsNewUiEnabled()
          && this.followUp?.id
          && !this.isFollowupQuestionsCompletedForCurrentFollowUp) {
        this.localStorageService.setValue('fupQuestionsActive', this.followUp.id);
      }
    }
  }

  async navigateToFollowUpChat(): Promise<void> {
    if (this.checkAndFireFollowUpQuestionsSheet()) return;
    await this.routeToFollowUpChat();
  }

  private async routeToFollowUpChat(): Promise<void> {
    const followUpId = this.selfCheckupService.getFollowUpId(this.followUp)
      ?? this.localStorageService.getValue('currentProgressFollowUpId');

    if (followUpId) {
      await this.selfCheckupService.navigateToFollowUpChat(followUpId);
      return;
    }
    await this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  checkAndFireFollowUpQuestionsSheet(): boolean {
    if (!this.experimentService.isFupQuestionsNewUiEnabled()) return false;
    if ((this.followUp?.get?.('followUpCount') ?? 0) > 1) return false;
    if (this.fupQuestionsSheetOpened) return true;
    if (!this.followUp) return false;

    this.fupQuestionsSheetOpened = true;
    const data: FollowUpQuestionsSheetData = {
      followUp: this.followUp,
      user: this.connectionService.getActingUser(),
      userImageUrl: this.instantCheckup?.imagePath,
    };
    const sheetRef = this.bottomSheet.open(FollowUpQuestionsSheetComponent, {
      data,
      panelClass: 'fup-questions-sheet-panel',
      hasBackdrop: true,
      disableClose: false,
    });
    sheetRef.afterDismissed().subscribe((result: any): void => {
      this.fupQuestionsSheetOpened = false;
      if (result?.fallbackToChat) {
        this.routeToFollowUpChat();
      } else if (result?.completed) {
        this.deleteLocalStorageItem('fromSelfCheckupBanner');
        this.deleteLocalStorageItem('moreInsightsBottomsheetShown');
        this.deleteLocalStorageItem('fupQuestionsCompleted');
        this.navigateToHome();
      }
    });
    return true;
  }

  get isFollowupQuestionsCompletedForCurrentFollowUp(): boolean {
    const followUpId = this.followUp?.id;
    if (!followUpId) return false;
    return this.localStorageService.getValue('fupQuestionsCompleted') === followUpId;
  }

  navigateToHomeAndClearFollowupFlags(): void {
    this.deleteLocalStorageItem('fromSelfCheckupBanner');
    this.deleteLocalStorageItem('moreInsightsBottomsheetShown');
    this.deleteLocalStorageItem('fupQuestionsCompleted');
    this.navigateToHome();
  }

  async shouldOpenReacquisitionUserBottomSheets(): Promise<boolean> {
    if (!this.isReacquisitionUser) return false;
    const bottomSheets = [
      {
        validation: async (): Promise<boolean> => await this.instantCheckupService.shouldShowRequestPhotoBottomSheet(this.user, this.instantCheckup?.type),
        open: () => this.reAcquisitionService.openRequestPhotoBottomSheet(this.instantCheckup?.type, this.instantCheckup?.imagePath, () => this.goCheckAgain()),
      },
      {
        validation: (): boolean =>  this.reAcquisitionService.getIsPhotoTaken() && !this.reAcquisitionService.getIsPendingCallCreated(),
        open: () => this.reAcquisitionService.openReacquisitionUserQueryBottomSheet(this.user),
      },
      {
        validation: (): boolean => this.reAcquisitionService.getIsPendingCallCreated(),
        open: () => this.reAcquisitionService.openReacquisitionWaitingForCallBottomSheet(this.user),
      }
    ]
    for (const bottomSheet of bottomSheets) {
      const shouldShow = await bottomSheet.validation();
      if (shouldShow) {
        bottomSheet.open();
        return true;
      }
    }
    return false;
  }


  get isHairCheckup(): boolean {
    const Type = ApiClientConstant.InstantCheckup.Type;
    return [Type.HAIR_FRONT, Type.HAIR_TOP].includes(this.instantCheckup?.type);
  }

  get logoImageSrc(): string {
    return this.isHairCheckup
      ? 'https://cdn.cureskin.com/app/img/curehair-logo.png'
      : 'https://cdn.cureskin.com/app/img/cureskin-logo.png';
  }

  get headerType(): HeaderTypeEnum {
    if (!this.instantCheckup) {
      return HeaderTypeEnum.SKIN_ANALYSIS_RESULT;
    }

    const isFacePhoto = [
      ApiClientConstant.InstantCheckup.Type.FRONT_FACE,
      ApiClientConstant.InstantCheckup.Type.SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.LEFT_SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.RIGHT_SIDE_FACE
    ].includes(this.instantCheckup.type);

    if (this.fromSelfCheckupBanner) {
      return HeaderTypeEnum.DATE;
    }

    if (this.isHairCheckup || isFacePhoto) {
      return HeaderTypeEnum.CURESKIN_LOGO;
    }

    return HeaderTypeEnum.SKIN_ANALYSIS_RESULT;
  }

  get footerLayout(): FooterLayout {
    // Create a dependency hash
    const currentDeps = `${this.fromSelfCheckupBanner}-${this.handleBlurPhotosExperiment}-${this.isInvalidPhoto}-${this.blurWithoutConcernsDetected}-${this.blurWithConcernsDetected}-${this.isFollowupQuestionsCompletedForCurrentFollowUp}`;

    // Only recompute if dependencies changed
    if (currentDeps !== this._footerLayoutDependencies) {
      this._footerLayoutDependencies = currentDeps;
      this._footerLayout = this.computeFooterLayout();
    }

    return this._footerLayout;
  }

  private computeFooterLayout(): FooterLayout {
    if (this.fromSelfCheckupBanner) {
      return this.getSelfCheckupBannerLayout();
    }
    if (this.handleBlurPhotosExperiment) {
      return this.getBlurPhotosExperimentLayout();
    }
    return this.getDefaultLayout();
  }


  private getSelfCheckupBannerLayout(): FooterLayout {
    if (this.isInvalidPhoto) {
      return {
        buttons: [{
          variant: ButtonVariant.PRIMARY,
          i18nKey: I18nKey.RE_TAKE_PHOTO,
          action: () => this.goCheckAgain(),
          shouldShow: true,
          cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
        }],
        containerVariant: FooterContainerVariant.DEFAULT
      };
    }

    const primaryButton = this.isFollowupQuestionsCompletedForCurrentFollowUp
      ? {
        variant: ButtonVariant.PRIMARY,
        i18nKey: I18nKey.CONTINUE,
        action: (): void => this.navigateToHomeAndClearFollowupFlags(),
        shouldShow: true,
        // tw-text-500 matches the larger Continue size used by the other
        // footer layouts. Share with Doctor stays at the base size because
        // its longer label needs the extra room within the half-width slot.
        cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500',
      }
      : {
        variant: ButtonVariant.PRIMARY,
        i18nKey: I18nKey.SHARE_WITH_DOCTOR,
        action: (): Promise<void> => this.navigateToFollowUpChat(),
        shouldShow: true,
        cssClasses: 'tw-bg-teal-600 tw-text-white',
      };

    return {
      buttons: [
        {
          variant: ButtonVariant.SECONDARY,
          i18nKey: I18nKey.RE_TAKE_PHOTO,
          action: () => this.goCheckAgain(),
          shouldShow: true,
          cssClasses: 'tw-border tw-border-teal-600 tw-text-teal-600'
        },
        primaryButton,
      ],
      containerVariant: FooterContainerVariant.SELF_CHECKUP
    };
  }

  private getBlurPhotosExperimentLayout(): FooterLayout {
    if (this.blurWithoutConcernsDetected) {
      return this.getBlurWithoutConcernsLayout();
    }
    if (this.blurWithConcernsDetected) {
      return this.getBlurWithConcernsLayout();
    }

    return this.getDefaultLayout();
  }

  private getBlurWithoutConcernsLayout(): FooterLayout {
    if (this.isInvalidPhoto) {
      return {
        buttons: [{
          variant: ButtonVariant.PRIMARY,
          i18nKey: I18nKey.RE_TAKE_PHOTO,
          action: () => this.goCheckAgain(),
          shouldShow: true,
          cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
        }],
        containerVariant: FooterContainerVariant.DEFAULT
      };
    }

    return {
      buttons: [
        {
          variant: ButtonVariant.SECONDARY,
          i18nKey: I18nKey.CONTINUE,
          action: () => this.continueToNext(),
          shouldShow: true,
          cssClasses: 'tw-bg-transparent tw-text-teal-600 tw-border tw-border-teal-600 tw-text-500'
        },
        {
          variant: ButtonVariant.PRIMARY,
          i18nKey: I18nKey.RETAKE,
          action: () => this.goCheckAgain(),
          shouldShow: this.isFaceOrHairWithDetection(),
          cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
        }
      ],
      containerVariant: FooterContainerVariant.DEFAULT
    };
  }

  private getBlurWithConcernsLayout(): FooterLayout {
    return {
      buttons: [
        {
          variant: ButtonVariant.SECONDARY,
          i18nKey: I18nKey.RETAKE,
          action: () => this.goCheckAgain(),
          shouldShow: this.isFaceOrHairWithDetection(),
          cssClasses: 'tw-bg-transparent tw-text-teal-600 tw-border tw-border-teal-600 tw-text-500'
        },
        {
          variant: ButtonVariant.PRIMARY,
          i18nKey: I18nKey.CONTINUE,
          action: () => this.continueToNext(),
          shouldShow: true,
          cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
        }
      ],
      containerVariant: FooterContainerVariant.DEFAULT
    };
  }

  private getDefaultLayout(): FooterLayout {
    if (this.isInvalidPhoto) {
      return {
        buttons: [{
          variant: ButtonVariant.PRIMARY,
          i18nKey: I18nKey.RE_TAKE_PHOTO,
          action: () => this.goCheckAgain(),
          shouldShow: true,
          cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
        }],
        containerVariant: FooterContainerVariant.DEFAULT
      };
    }

    const textSizeClass = this.user?.get('languagePreference') === this.appConfig.Shared.Languages.TA ? 'tw-text-200' : 'tw-text-500';
    const clampFontSize = this.shouldUseClampedFontSize() ? 'tw-text-[clamp(11px,4vw,18px)]' : '';
    return {
      buttons: [
        {
          variant: ButtonVariant.SECONDARY,
          i18nKey: this.getCheckAgainI18nKey(),
          action: () => this.goCheckAgain(),
          shouldShow: true,
          cssClasses: `tw-bg-transparent tw-text-teal-600 tw-border tw-border-black ${textSizeClass} ${clampFontSize}`
        },
        {
          variant: ButtonVariant.PRIMARY,
          i18nKey: I18nKey.CONTINUE,
          action: () => this.continueToNext(),
          shouldShow: true,
          cssClasses: 'tw-bg-teal-600 tw-text-white tw-text-500'
        }
      ],
      containerVariant: FooterContainerVariant.DEFAULT
    };
  }

  private getCheckAgainI18nKey(): I18nKey {
    if (this.isFaceOrHairWithDetection()) {
      return I18nKey.CHECK_AGAIN;
    }
    if (this.isHairWithoutDetection() || this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.INVALID) {
      return I18nKey.RE_TAKE_PHOTO;
    }
    if (this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.REPORT) {
      return I18nKey.CHANGE;
    }
    return I18nKey.RE_TAKE_PHOTO;
  }


  private isFaceOrHairWithDetection(): boolean {
    if (!this.instantCheckup?.type) return false;
    const isFace = this.instantCheckup.type.includes(ApiClientConstant.InstantCheckup.Type.FACE);
    const isHairFrontWithDetection = this.instantCheckup.type === ApiClientConstant.InstantCheckup.Type.HAIR_FRONT &&
      this.instantCheckup?.aiResponse?.hairDetected === true;
    return isFace || isHairFrontWithDetection;
  }


  private isHairWithoutDetection(): boolean {
    if (!this.instantCheckup?.type || !this.instantCheckup?.aiResponse) return false;
    const isHairFrontOrTop = [
      ApiClientConstant.InstantCheckup.Type.HAIR_FRONT,
      ApiClientConstant.InstantCheckup.Type.HAIR_TOP
    ].includes(this.instantCheckup?.type);
    return isHairFrontOrTop && !this.instantCheckup?.aiResponse?.hairDetected;
  }

  private shouldUseClampedFontSize(): boolean {
    return this.isHairWithoutDetection() || this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.INVALID;
  }

  private setDefaultHairTopCroppingConfig(): void {
    if (!this.croppingConfig && this.image) {
      const defaultScale = Math.min(
        (this.windowRef.nativeWindow.innerWidth * 0.9) / this.image.width,
        (this.windowRef.nativeWindow.innerHeight * 0.6) / this.image.height
      );
      this.croppingConfig = { left: 0, top: 0, ratio: defaultScale, offsetX: 0, offsetY: 0 };
    }
  }

  private updateHairTopCroppingConfig(): void {
    if (!this.croppingConfig || !this.image) return;

    setTimeout((): void => {
      const canvasElement = this.windowRef.nativeWindow.document.querySelector('canvas[appdrawimage]') as HTMLCanvasElement
        || this.windowRef.nativeWindow.document.querySelector('canvas') as HTMLCanvasElement;

      if (!canvasElement || !this.image || canvasElement.clientWidth <= 0 || canvasElement.clientHeight <= 0) {
        return;
      }

      const scale = Math.min(canvasElement.clientWidth / this.image.width, canvasElement.clientHeight / this.image.height);
      const displayedWidth = this.image.width * scale;
      const displayedHeight = this.image.height * scale;
      const offsetX = (canvasElement.clientWidth - displayedWidth) / 2;
      const offsetY = (canvasElement.clientHeight - displayedHeight) / 2;

      this.croppingConfig = {
        left: 0,
        top: 0,
        ratio: scale,
        offsetX: offsetX,
        offsetY: offsetY
      };
      this.changeDetection.detectChanges();
    }, 100);
  }

  private processScalpDetections(): void {
    const results: AiDetectionResult[] = [...(this.instantCheckup?.aiResponse?.result || [])];
    const scalpDetections = results.filter((result: AiDetectionResult): boolean =>
      result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED && result.class === 'scalp-segment',
    );

    if (scalpDetections.length === 0) return;

    scalpDetections.forEach((detection: AiDetectionResult): void => {
      try {
        const scalpMasks: number[][][] = JSON.parse(detection.scalp_masks || '[]');
        const polygonSequences: number[][][] = scalpMasks.map((polygon: number[][]): number[][] => polygon);

        const scalpResult: AiDetectionResult = {
          ...detection,
          PolygonPointsSequences: polygonSequences,
        };

        this.results.push(scalpResult);
      } catch (error) {
        console.error('Error parsing scalp_masks:', error);
      }
    });
  }

  private applyTealColorToHairDetections(): void {
    this.results = this.results.map((result: DisplayDetectionResult): DisplayDetectionResult => {
      if (!result.PolygonPointsSequences) return result;
      const updatedSequences: PolygonEntry[] = result.PolygonPointsSequences.map(
        (polygon: PolygonEntry): PolygonEntry => {
          if ('points' in polygon) {
            return polygon;
          }
          return { color: TEAL_DETECTION_COLOR, points: polygon };
        },
      );
      return { ...result, PolygonPointsSequences: updatedSequences };
    });
  }

  hasScalpDetections(): boolean {
    if (this.instantCheckup?.type !== ApiClientConstant.InstantCheckup.Type.HAIR_TOP) {
      return false;
    }

    const results: AiDetectionResult[] = [...(this.instantCheckup?.aiResponse?.result || [])];
    const scalpDetections = results.filter((result: AiDetectionResult): boolean =>
      result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED && result.class === 'scalp-segment',
    );

    if (scalpDetections.length === 0) return false;

    // Check if any detection has non-empty scalp_masks
    return scalpDetections.some((detection: AiDetectionResult): boolean => {
      try {
        if (!detection?.scalp_masks) return false;
        const scalpMasks = JSON.parse(detection.scalp_masks || '[]');
        return Array.isArray(scalpMasks) && scalpMasks.length > 0;
      } catch (error) {
        return false;
      }
    });
  }

  isScalpFilledView(): boolean {
    return this.hairAiDetectionTealColor()
      && this.showDetectionViewTab()
      && this.isHairTopType
      && this.scalpViewMode() === this.appConfig.Shared.InstantCheckup.Detections.View.FILLED;
  }

  isDetectionOutlined(): boolean {
    if (!this.hairAiDetectionTealColor()) return false;
    if (this.isHairFrontType) return true;
    return this.showDetectionViewTab()
      && this.isHairTopType
      && this.scalpViewMode() === this.appConfig.Shared.InstantCheckup.Detections.View.OUTLINE;
  }

  shouldShowMintGlow(detection: DisplayDetectionResult): boolean {
    if (!this.isDetectionOutlined()) return false;
    return detection?.class !== this.appConfig.Shared.InstantCheckup.Concerns.HAIRLINE;
  }

  getScalpPillClasses(target: ScalpViewMode): Record<string, boolean> {
    const selected = this.scalpViewMode() === target;
    return {
      'tw-text-gray-50': selected,
      'tw-text-gray-900': !selected,
    };
  }

  toggleScalpView(): void {
    if (!this.hairAiDetectionTealColor()
      || !this.showDetectionViewTab()
      || !this.isHairTopType
      || !this.hasScalpDetections()) {
      return;
    }
    const View = this.appConfig.Shared.InstantCheckup.Detections.View;
    const next = this.scalpViewMode() === View.FILLED ? View.OUTLINE : View.FILLED;
    this.applyScalpViewMode(next, 'image');
  }

  applyScalpViewMode(target: ScalpViewMode, source: 'toggle' | 'image'): void {
    if (this.scalpViewMode() === target) return;
    this.eventLogger.cleverTapEvent(
      'click',
      JSON.stringify({ name: 'hair_scalp_detection_toggled', source, viewMode: target }),
    );
    this.scalpViewMode.set(target);
  }

  determineIsHairTopType(): void {
    const experimentEnabled = !!this.hairScalpDetectionExperiment
    this.showNoScalpDetectionCard = this.hairScalpDetectionExperiment?.variant?.showNoDetectionCard
    this.isHairTopType = this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.HAIR_TOP && experimentEnabled
    this.isHairFrontType = this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.HAIR_FRONT
  }

  get showFooter(): boolean {
    if (this.blurWithoutConcernsDetected) {
      return false;
    }

    if (this.results?.length) {
      return true;
    }

    if (this.instantCheckup?.type === ApiClientConstant.InstantCheckup.Type.HAIR_FRONT) {
      return this.isTempleBaldnessDetected || this.isHairlineDetected;
    }

    if (this.isHairTopType) {
      return this.hasScalpDetections() || this.showNoScalpDetectionCard;
    }

    return false;
  }

  get showPhotoReviewDisclaimer() {
    return this.instantCheckup && this.instantCheckupStatusBannerState &&
      (!this.isHairTopType || (!this.hasScalpDetections() && !this.showNoScalpDetectionCard));
  }

  protected readonly ApiClientConstant = ApiClientConstant;
}
